import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Table, Form, Button } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import { FaPeopleGroup, FaPlus } from "react-icons/fa6";
import { MdExitToApp } from "react-icons/md";
import { BASE_URL } from '../../utils/constants';
import CustomDateRangePicker from '../../components/CustomDateRangePicker';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { AiFillCaretUp, AiFillCaretDown } from "react-icons/ai";
import Pagination from '../../components/Pagination/Pagination'; // Import the reusable Pagination component

import "./Patients.scss"

const Patients = ({ user, logUserOut, dateRange, setDateRange }) => {
  const [loading, setLoading] = useState(true)
  const [searchValue, setSearchValue] = useState("")
  const [showEmail, setShowEmail] = useState(false)
  const [patients, setPatients] = useState([])
  const [filteredPatients, setFilteredPatients] = useState(null)
  const [startDate, endDate] = dateRange;
  const [sorting, setSorting] = useState([{ field: 'custom_patient_id', ascending: true }])
  const navigate = useNavigate();

  // New state variables for UI-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  useEffect(() => {
    const getPatientsByEventDate = async (dateRange) => {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
      
      const body = {
        startDate: dateRange[0],
        endDate: dateRange[1]
      }
      try {
        if((startDate !== null && endDate !== null) || (startDate === null && endDate === null)) {
          setLoading(true)
          const response = await axios.post(`${BASE_URL}/api/v1/patient/by-event-date`, body, config)
          setPatients(response.data.patients)
          setLoading(false)
        }
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getPatientsByEventDate(dateRange)
  }, [dateRange])

  
  useEffect(() => {
    if(searchValue !== "") {
      const filtered = patients.filter(patient => { 
        return patient.custom_patient_id.toLowerCase().includes(searchValue) || patient.email.toLowerCase().includes(searchValue) || patient.study.toLowerCase().includes(searchValue); 
      });
      setFilteredPatients(filtered)
    }
    else {
      setFilteredPatients(null)
    }
    setCurrentPage(1) // Reset to page 1 on search
  }, [searchValue, patients])

  // Create a computed value for sorted data instead of modifying state
  const sortedData = React.useMemo(() => {
    const multiSort = (a, b) => {
      for (const sortInfo of sorting) {
        const { field, ascending } = sortInfo;
        
        let valA = a[field] ?? (ascending ? Infinity : -Infinity);
        let valB = b[field] ?? (ascending ? Infinity : -Infinity);

        // Handle date fields specifically
        if (field === 'event_date') {
          valA = new Date(a[field]);
          valB = new Date(b[field]);
        }

        if (valA < valB) {
          return ascending ? -1 : 1;
        }
        if (valA > valB) {
          return ascending ? 1 : -1;
        }
      }
      return 0;
    };

    const dataToSort = filteredPatients || patients;
    return [...dataToSort].sort(multiSort);
  }, [sorting, patients, filteredPatients])

  const handleSort = (field, event) => {
    const isShiftPressed = event.shiftKey;
    const isCtrlPressed = event.ctrlKey || event.metaKey;

    if (isCtrlPressed) {
      setSorting([{ field: 'custom_patient_id', ascending: true }]);
      return;
    }

    setSorting(prevSorting => {
      const existingSortIndex = prevSorting.findIndex(s => s.field === field);

      if (existingSortIndex > -1) {
        return prevSorting.map((sortItem, index) => {
          if (index === existingSortIndex) {
            return { ...sortItem, ascending: !sortItem.ascending };
          }
          return sortItem;
        });
      } else {
        const newSort = { field, ascending: true };
        if (isShiftPressed) {
          return [...prevSorting, newSort];
        } else {
          return [newSort];
        }
      }
    });
  };

  const renderSortIndicator = (field) => {
    const sortIndex = sorting.findIndex(s => s.field === field);
    if (sortIndex === -1) return null;

    const { ascending } = sorting[sortIndex];
    return (
      <>
        {ascending ? <AiFillCaretUp /> : <AiFillCaretDown />}
        {sorting.length > 1 && <span className="sort-priority">{sortIndex + 1}</span>}
      </>
    );
  };

  const resetSorting = () => {
    setSorting([{ field: 'custom_patient_id', ascending: true }]);
  };

  
  // Logic to handle UI-side pagination
  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedData.slice(startIndex, endIndex);

  return (
    <div className="patients">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <div className="patients-header">
            <div className="title-row">
              <h2 className="logo"><FaPeopleGroup /> Patients</h2>      
              <Button variant="success" onClick={() => navigate("/patients/new")}>
                <FaPlus /> Enrol Patient
              </Button>
            </div>
            
            <div className="filters-row">
              <Form.Group className="form-group search">
                <Form.Control
                  type="text"
                  placeholder="Search patients..."
                  onChange={(event) => setSearchValue(event.target.value.toLowerCase())}
                />
              </Form.Group>

              <Form.Group className="form-group date-picker">
                <CustomDateRangePicker
                  dateRange={dateRange}
                  onChange={setDateRange}
                  placeholder="Filter by procedure date"
                  className="procedure-date-picker"
                />
              </Form.Group>
              
              {sorting.length > 1 && (
                <Form.Group className="form-group reset-sort">
                  <Button variant="outline-primary" size="sm" onClick={resetSorting} className="reset-sort-btn">
                    Reset Sort
                  </Button>
                </Form.Group>
              )}
            </div>
          </div>

          <div className='patients-table'>
            <Table striped bordered={false} hover>
              <thead>
                <tr>
                  <th className='custom-id' onClick={(e) => handleSort("custom_patient_id", e)}>Patient ID {renderSortIndicator("custom_patient_id")}</th>
                  <th className='email' onClick={(e) => handleSort("email", e)}>Email {renderSortIndicator("email")} <span className='show-hide-anchor' onClick={(e) => {e.stopPropagation(); setShowEmail(!showEmail)}}>({showEmail? "hide" : "show"})</span></th>
                  <th className='study' onClick={(e) => handleSort("study", e)}>Study {renderSortIndicator("study")}</th>
                  <th className='event-date' onClick={(e) => handleSort("event_date", e)}>Procedure Date {renderSortIndicator("event_date")}</th>
                  <th className='ps'>PS</th>
                  <th className='ss'>SS</th>
                  <th className='button'></th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((patient, index) => {
                  return (
                    <tr key={index}>
                      <td className="custom-id">{patient.custom_patient_id}</td>
                      <td className="email">{showEmail && patient.email}</td>
                      <td className="study">{patient.study}</td>
                      <td className="event-date">{moment(patient.event_date).format("DD/MM/YYYY")}</td>
                      <td className="ps"><span className={
                        // Green dot: Survey is completed
                        patient.preTreatmentSurveyCompleted ? "green-dot" :
                        // Purple dot: Survey not filled out and we're at or past surgery date
                        moment().isSameOrAfter(moment(patient.event_date), 'day') ? "purple-dot" :
                        // Red dot: Less than 5 days remaining before surgery
                        moment(patient.event_date).diff(moment(), 'days') < 5 ? "red-dot" :
                        // Yellow dot: Survey available with more than 5 days remaining
                        "yellow-dot"
                      }></span></td>
                      <td className="ss"><span className={patient.containsIncompleteSurgeryData ? "red-dot" : "green-dot"}></span></td>
                      <td className="button"><Button variant="primary" onClick={() => navigate(`/patients/${patient.custom_patient_id}`)}>
                        <MdExitToApp />
                      </Button></td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </>)}    
    </div>
  )
}

export default Patients